'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye } from 'lucide-react';
import { Vehicle, Booking, ActivePageView } from '@/types';
import { apiAsset, DEMO_MODE } from '@/config';
import { INITIAL_BOOKINGS } from '@/lib/data';
import { api } from '@/lib/api';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { VehiclesCatalogView } from '@/components/site/VehiclesCatalogView';
import { BookingModal } from '@/components/site/BookingModal';
import { UserProfileView } from '@/components/site/UserProfileView';
import { LocationsView } from '@/components/site/LocationsView';
import { DealsView } from '@/components/site/DealsView';
import { ServicesView } from '@/components/site/ServicesView';
import { AboutContactView } from '@/components/site/AboutContactView';
import { VehicleDetailPage } from '@/components/site/VehicleDetailPage';
import { CurrencyProvider } from '@/components/site/CurrencyContext';
import { PublicBrandProvider } from '@/components/site/PublicBrandProvider';
import { StorefrontDataProvider } from '@/lib/storefront/storefront-data';
import { ThemeFooter, ThemeHome } from '@/components/site/theme/ThemeHome';
import { ClassicHome } from '@/components/site/designs/ClassicHome';
import { EditorialHome } from '@/components/site/designs/EditorialHome';
import { RoadtripHome } from '@/components/site/designs/RoadtripHome';
import type { HomeDesignProps } from '@/components/site/designs/home-props';
import { FALLBACK_BRAND, isLocalizedContact, type PublicBrand } from '@/lib/public-brand';
import { parseStorefront, type StorefrontDocument } from '@/lib/storefront/document';
import { DEFAULT_APPEARANCE, appearanceCssVars, ensureFontLoaded, type BrandColors } from '@/lib/storefront/appearance';
import { designById, sectionEnabled } from '@/lib/storefront/designs';
import { isPreviewMode, readPreviewDocument, subscribePreviewDocument } from '@/lib/storefront/preview';
import { ensureThemeFont, isPlatformPreviewOrigin, parsePublicTheme, themeCssVars, THEME_PREVIEW_MESSAGE, THEME_PREVIEW_READY, type PublicTheme, type ThemeAgency } from '@/lib/storefront/theme-document';
import { useLocale } from '@/lib/i18n';
import { AssistantWidget } from '@/components/site/AssistantWidget';
import { CopyArticle, FaqArticle, VehiclePageStatus } from '@/components/site/SiteArticles';
import { isBuiltInPath, pathForView, vehicleIdFromPath, viewFromPath } from '@/lib/site/routes';
import { useSiteJsonLd } from '@/lib/site/use-site-json-ld';
import {
  PublishedHome,
  PublishedHomeSkeleton,
  usePublishedPage,
  usePublishedPageMetadata,
} from '@/components/site/PublishedHome';

function publishedVehicle(vehicle: Vehicle): Vehicle {
  return {
    ...vehicle,
    imageUrl: apiAsset(vehicle.imageUrl ?? ''),
    galleryImages: Array.isArray(vehicle.galleryImages) ? vehicle.galleryImages.map((url) => apiAsset(url)) : [],
  };
}

const SETTINGS_CACHE = 'fleetava_public_settings:';
const DRAFT_PREVIEW_KEY = 'fleetava_draft_preview_token';

/**
 * Reads a signed draft-preview token from the URL (or the current tab) and
 * removes it from the address bar. Keeping it in sessionStorage means normal
 * in-page navigation keeps previewing drafts without passing the token around
 * in every link or leaking it through the Referer header.
 */
function readDraftPreviewToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('draftPreview');
    if (fromUrl) {
      window.sessionStorage.setItem(DRAFT_PREVIEW_KEY, fromUrl);
      params.delete('draftPreview');
      const query = params.toString();
      const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      window.history.replaceState(window.history.state, '', next);
      return fromUrl;
    }
    return window.sessionStorage.getItem(DRAFT_PREVIEW_KEY);
  } catch {
    return null;
  }
}

function readSettingsCache(): Record<string, unknown> | null {
  try {
    const host = typeof window === 'undefined' ? '' : window.location.host;
    const raw = window.sessionStorage.getItem(`${SETTINGS_CACHE}${host}`);
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function writeSettingsCache(data: Record<string, unknown>): void {
  try {
    const host = typeof window === 'undefined' ? '' : window.location.host;
    window.sessionStorage.setItem(`${SETTINGS_CACHE}${host}`, JSON.stringify(data));
  } catch {
    /* cache is best-effort */
  }
}

function clearSettingsCache(): void {
  try {
    const host = typeof window === 'undefined' ? '' : window.location.host;
    window.sessionStorage.removeItem(`${SETTINGS_CACHE}${host}`);
  } catch {
    /* cache is best-effort */
  }
}

function brandFromSettings(data: Record<string, unknown>): PublicBrand | null {
  if (typeof data.name !== 'string' || !data.name) return null;
  return {
    name: data.name,
    shortName: typeof data.shortName === 'string' && data.shortName ? data.shortName : data.name,
    city: typeof data.city === 'string' ? data.city : '',
    address: typeof data.address === 'string' ? data.address : '',
    phone: typeof data.phone === 'string' ? data.phone : '',
    whatsapp: typeof data.whatsapp === 'string' ? data.whatsapp : '',
    email: typeof data.email === 'string' ? data.email : '',
    logoUrl: typeof data.logoUrl === 'string' ? data.logoUrl : '',
    localizedContact: isLocalizedContact(data.localizedContact) ? data.localizedContact : {},
  };
}

function colorsFromSettings(data: Record<string, unknown>): BrandColors {
  return {
    primaryColor: typeof data.primaryColor === 'string' ? data.primaryColor : '',
    secondaryColor: typeof data.secondaryColor === 'string' ? data.secondaryColor : '',
  };
}

export default function PublicSite() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentView = viewFromPath(location.pathname);
  const vehicleId = vehicleIdFromPath(location.pathname);
  const cachedSettings = React.useMemo(readSettingsCache, []);
  // A published builder page owns its URL. Single-segment paths map to a page
  // slug (`/fleet` -> `fleet`, `/` -> `home`); deeper paths keep the built-in
  // storefront views (vehicle details, legal pages, signing).
  // Any one-segment path is a candidate page. The API decides whether a page
  // exists; invalid/reserved slugs simply render the storefront not-found view.
  const singleSegment = /^\/([^/]+)\/?$/.exec(location.pathname);
  const pageSlug = location.pathname === '/' ? 'home' : singleSegment ? singleSegment[1] : null;
  const builtIn = isBuiltInPath(location.pathname);
  // Built-in paths win, except `/` which belongs to the builder home page.
  // Do not even ask for a builder page on `/about`, `/vehicles`, etc.
  const builderPageSlug = pageSlug !== null && (!builtIn || pageSlug === 'home') ? pageSlug : null;
  const [draftPreviewToken, setDraftPreviewToken] = useState<string | null>(readDraftPreviewToken);
  const isDraftPreview = Boolean(draftPreviewToken);
  const published = usePublishedPage(builderPageSlug, draftPreviewToken);
  const publishedSections = published.sections;
  // A custom page shares the header and footer the agency built on its home
  // page. Its own copies (created by older templates) are ignored so editing
  // the home navigation updates every page.
  const isCustomNonHome = pageSlug !== null && pageSlug !== 'home' && published.page !== null;
  const pageBodySections = isCustomNonHome
    ? publishedSections.filter((section) => section.category !== 'header' && section.category !== 'footer')
    : publishedSections;
  // Built-in paths stay built-in even if an old row with the same slug exists.
  // The home page is the one builder page that owns `/`.
  const hasCustomPage = pageSlug !== null
    && published.page !== null
    && publishedSections.length > 0
    && (pageSlug === 'home' || !builtIn);
  const previewUnavailable = isDraftPreview && !published.loading && published.error !== null;
  // Built-in views (vehicles, profile, legal pages...) keep their own content,
  // but they should wear the header and footer the agency built on its home
  // page. Load the chrome for every non-home path, even custom pages, so the
  // built-in fallback never flashes while the page request is in flight.
  const needsBuilderChrome = pageSlug !== 'home';
  const homeChrome = usePublishedPage(needsBuilderChrome ? 'home' : null, draftPreviewToken);
  const homeChromeSections = homeChrome.sections;
  const builderHeader = homeChromeSections.find((section) => section.category === 'header') ?? null;
  const builderFooter = [...homeChromeSections].reverse().find((section) => section.category === 'footer') ?? null;
  const hasBuilderChrome = needsBuilderChrome && builderHeader !== null;
  const hasHomeChrome = builderHeader !== null || builderFooter !== null;
  const usesSharedChrome = isCustomNonHome && hasHomeChrome;

  const go = (view: ActivePageView, nextVehicleId?: string) => {
    const search = isPreviewMode() ? '?preview=1' : '';
    navigate(`${pathForView(view, nextVehicleId)}${search}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exitDraftPreview = () => {
    try {
      window.sessionStorage.removeItem(DRAFT_PREVIEW_KEY);
    } catch {
      /* best-effort */
    }
    setDraftPreviewToken(null);
    const params = new URLSearchParams(window.location.search);
    params.delete('draftPreview');
    const query = params.toString();
    const next = `${window.location.pathname}${query ? `?${query}` : ''}`;
    const current = `${window.location.pathname}${window.location.search}`;
    if (next === current) window.location.reload();
    else window.location.assign(next);
  };
  // Agency currency (public) — transaction currency for every website price.
  const [currency, setCurrency] = useState(() =>
    cachedSettings && typeof cachedSettings.currency === 'string' ? cachedSettings.currency : 'MAD',
  );
  // Visitor display currency (header selector) — null = follow agency.
  const [displayCurrency, setDisplayCurrency] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const saved = localStorage.getItem('fleetava_display_currency');
        if (saved) setDisplayCurrency(saved);
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const handleDisplayCurrency = (code: string) => {
    setDisplayCurrency(code);
    try {
      localStorage.setItem('fleetava_display_currency', code);
    } catch {}
  };
  const [rates, setRates] = useState<{ rateEur: number; rateUsd: number } | null>(() => {
    if (!cachedSettings) return null;
    const eur = Number(cachedSettings.rateEur);
    const usd = Number(cachedSettings.rateUsd);
    if (!(eur > 0) && !(usd > 0)) return null;
    return { rateEur: eur > 0 ? eur : 10.8, rateUsd: usd > 0 ? usd : 10.0 };
  });
  const [brand, setBrand] = useState<PublicBrand>(() => (cachedSettings && brandFromSettings(cachedSettings)) || FALLBACK_BRAND);
  // Colours from the agency profile: they stay in force until the appearance
  // editor stores the agency's own choice.
  const [brandColors, setBrandColors] = useState<BrandColors>(() => (cachedSettings ? colorsFromSettings(cachedSettings) : {}));
  const [storefront, setStorefront] = useState<StorefrontDocument | null>(() =>
    cachedSettings ? parseStorefront(cachedSettings.storefront) : null,
  );
  const [publishedTheme, setPublishedTheme] = useState<PublicTheme | null>(() =>
    cachedSettings ? parsePublicTheme(cachedSettings.theme) : null,
  );
  // The first public-settings answer carries the agency's colours. Painting the
  // home before it lands shows the default orange palette for a frame, then
  // recolours the whole page — the second half of the visible "glitch".
  const [settingsReady, setSettingsReady] = useState(() => cachedSettings !== null);
  const [agencyUnavailable, setAgencyUnavailable] = useState(false);

  // A single-segment path with no published builder page is a storefront 404.
  // Wait for the page and the palette before deciding, otherwise the skeleton
  // and the not-found view can appear together.
  const unknownSingleSegment = pageSlug !== null
    && pageSlug !== 'home'
    && !hasCustomPage
    && !published.loading
    && !previewUnavailable
    && settingsReady
    && !builtIn;

  // While the page (and the agency palette it is painted with) is still being
  // fetched we show a neutral placeholder instead of the built-in template,
  // which would otherwise be replaced a moment later and read as a flash.
  const pagePending = pageSlug !== null && (published.loading || !settingsReady);
  // A loaded custom page can paint while the agency palette is still settling;
  // do not also show the skeleton underneath it.
  const showCustomPage = hasCustomPage && published.page !== null;
  const showBuiltIn = !hasCustomPage && !pagePending && !unknownSingleSegment && !previewUnavailable;
  const showChrome = !hasCustomPage && !pagePending && !previewUnavailable;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/public-settings');
        if (cancelled) return;
        if (res.status === 403 || res.status === 404) {
          clearSettingsCache();
          setStorefront(null);
          setPublishedTheme(null);
          setBrandColors({});
          setAgencyUnavailable(true);
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setAgencyUnavailable(false);
        if (typeof data.currency === 'string') setCurrency(data.currency);
        const nextBrand = brandFromSettings(data);
        if (nextBrand) setBrand(nextBrand);
        setStorefront(parseStorefront(data.storefront));
        setPublishedTheme(parsePublicTheme(data.theme));
        setBrandColors(colorsFromSettings(data));
        writeSettingsCache(data);
        if (Number(data.rateEur) > 0 || Number(data.rateUsd) > 0) {
          setRates({
            rateEur: Number(data.rateEur) > 0 ? Number(data.rateEur) : 10.8,
            rateUsd: Number(data.rateUsd) > 0 ? Number(data.rateUsd) : 10.0,
          });
        }
      } catch {
        // Keep MAD fallback.
      } finally {
        if (!cancelled) setSettingsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // The appearance editor previews its unsaved draft through a same-origin
  // channel. When `?preview=1` is set, that draft replaces the published
  // storefront (copy and appearance alike) so nothing has to be saved first.
  const [previewStorefront, setPreviewStorefront] = useState<StorefrontDocument | null>(() =>
    isPreviewMode() ? readPreviewDocument() : null,
  );

  useEffect(() => {
    if (!isPreviewMode()) return;
    setPreviewStorefront(readPreviewDocument());
    return subscribePreviewDocument(setPreviewStorefront);
  }, []);

  const [previewTheme, setPreviewTheme] = useState<PublicTheme | null>(null);
  const brandRef = React.useRef(brand);
  brandRef.current = brand;

  useEffect(() => {
    if (!isPreviewMode()) return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || !isPlatformPreviewOrigin(event.origin) || event.data?.type !== THEME_PREVIEW_MESSAGE) return;
      const theme = parsePublicTheme(event.data.theme);
      if (!theme) return;
      setPreviewTheme(theme.agency.name === '' ? { ...theme, agency: agencyFromBrand(brandRef.current) } : theme);
    };
    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: THEME_PREVIEW_READY }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const site = previewStorefront ?? storefront;
  const appearance = site?.appearance ?? DEFAULT_APPEARANCE;
  const activeTheme = previewTheme ?? publishedTheme;
  const design = designById(activeTheme ? 'classic' : appearance.design);

  useEffect(() => {
    if (activeTheme) ensureThemeFont(activeTheme.tokens.font);
    else ensureFontLoaded(appearance.font);
  }, [activeTheme, appearance.font]);

  const effectiveBrand = useMemo<PublicBrand>(
    () => ({
      ...brand,
      shortName: appearance.logoText.trim() || brand.shortName,
      logoUrl: (activeTheme?.logoUrl || appearance.logoUrl).trim() || brand.logoUrl,
    }),
    [brand, appearance.logoText, appearance.logoUrl, activeTheme],
  );

  usePublishedPageMetadata(hasCustomPage ? published.page : null, effectiveBrand.name, isDraftPreview);
  useSiteJsonLd({
    enabled: !isPreviewMode() && !isDraftPreview,
    name: effectiveBrand.name,
    url: typeof window !== 'undefined' ? `${window.location.origin}/` : '',
    telephone: effectiveBrand.phone,
    email: effectiveBrand.email,
    address: effectiveBrand.address,
    city: effectiveBrand.city,
    logoUrl: effectiveBrand.logoUrl,
    description: hasCustomPage && typeof published.page?.settings?.description === 'string'
      ? published.page.settings.description
      : '',
  });

  // Vehicles come from the server API only — never paint demo fleet on the live site.
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [catalogError, setCatalogError] = useState(false);
  const [catalogReady, setCatalogReady] = useState(false);

  const refreshVehicles = async () => {
    try {
      const list = await api<Vehicle[]>('/api/vehicles');
      if (Array.isArray(list)) {
        setVehicles(list.map(publishedVehicle));
        setCatalogError(false);
      }
    } catch {
      setVehicles([]);
      setCatalogError(true);
    } finally {
      setCatalogReady(true);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api<Vehicle[]>('/api/vehicles');
        if (!cancelled && Array.isArray(list)) {
          setVehicles(list.map(publishedVehicle));
          setCatalogError(false);
        }
      } catch {
        if (!cancelled) {
          setVehicles([]);
          setCatalogError(true);
        }
      } finally {
        if (!cancelled) setCatalogReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Bookings/favorites: deterministic initial values (SSR-safe) then hydrate
  // from localStorage after mount. Never branch on `typeof window` during
  // render — that desyncs server HTML from the client (hydration error).
  const [bookings, setBookings] = useState<Booking[]>(INITIAL_BOOKINGS);
  const [favorites, setFavorites] = useState<string[]>(['car-3']);

  useEffect(() => {
    // Deferred to a callback (lint-safe): hydrates the SSR-safe initial
    // state with the guest's local data after mount.
    const t = setTimeout(() => {
      try {
        const saved = localStorage.getItem('fleetava_bookings');
        if (saved) setBookings(JSON.parse(saved));
      } catch {}
      try {
        const fav = localStorage.getItem('fleetava_favorites');
        if (fav) setFavorites(JSON.parse(fav));
      } catch {}
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Modals state
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedVehicleForBooking, setSelectedVehicleForBooking] = useState<Vehicle | null>(null);
  const [searchCriteria, setSearchCriteria] = useState<{
    location: string;
    pickupDate: string;
    pickupTime: string;
    dropoffDate: string;
    dropoffTime: string;
  }>({
    location: 'Miami International Airport (MIA)',
    pickupDate: '2026-10-18',
    pickupTime: '10:00 AM',
    dropoffDate: '2026-10-22',
    dropoffTime: '10:00 AM',
  });

  const selectedVehicleForPage = vehicleId
    ? vehicles.find((car) => car.id === vehicleId) ?? null
    : null;

  // A hero search lands on /vehicles with pickup/from/to in the URL. Carry
  // those values into the booking modal so "Book now" keeps the dates the
  // visitor just chose.
  useEffect(() => {
    if (currentView !== 'vehicles') return;
    const params = new URLSearchParams(location.search);
    const pickup = params.get('pickup')?.trim() ?? '';
    const from = params.get('from') ?? '';
    const to = params.get('to') ?? '';
    if (!pickup && !from && !to) return;
    setSearchCriteria((current) => ({
      ...current,
      ...(pickup ? { location: pickup } : {}),
      ...(from ? { pickupDate: from } : {}),
      ...(to ? { dropoffDate: to } : {}),
    }));
  }, [currentView, location.search]);

  // Guest-local persistence (bookings history + wishlist stay in localStorage).
  const saveBookings = (newBookings: Booking[]) => {
    setBookings(newBookings);
    try {
      localStorage.setItem('fleetava_bookings', JSON.stringify(newBookings));
    } catch {}
  };

  const saveFavorites = (newFavorites: string[]) => {
    setFavorites(newFavorites);
    try {
      localStorage.setItem('fleetava_favorites', JSON.stringify(newFavorites));
    } catch {}
  };

  // Handlers
  const handleToggleFavorite = (id: string) => {
    if (favorites.includes(id)) {
      saveFavorites(favorites.filter((fav) => fav !== id));
    } else {
      saveFavorites([...favorites, id]);
    }
  };

  const handleOpenBookingModal = (vehicle?: Vehicle) => {
    if (vehicle) {
      setSelectedVehicleForBooking(vehicle);
    } else if (!selectedVehicleForBooking) {
      // default to first available popular car (BMW or Corolla)
      setSelectedVehicleForBooking(vehicles[0]);
    }
    setBookingModalOpen(true);
  };

  const handleViewCarDetailPage = (vehicle: Vehicle) => {
    setSelectedVehicleForBooking(vehicle);
    go('vehicle-detail', vehicle.id);
  };

  const handleSearchSubmit = (criteria: {
    location: string;
    pickupDate: string;
    pickupTime: string;
    dropoffDate: string;
    dropoffTime: string;
    rentalType: string;
  }) => {
    setSearchCriteria(criteria);
    const params = new URLSearchParams();
    if (criteria.location.trim()) params.set('pickup', criteria.location.trim());
    if (criteria.pickupDate) params.set('from', criteria.pickupDate);
    if (criteria.dropoffDate) params.set('to', criteria.dropoffDate);
    if (isPreviewMode()) params.set('preview', '1');
    const query = params.toString();
    navigate(`/vehicles${query ? `?${query}` : ''}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // AI assistant handoff: prefill dates/location from the chat, open the
  // booking modal on the recommended car.
  const handleAssistantReserve = (
    vehicleId: string,
    prefill: { pickupDate?: string; dropoffDate?: string; location?: string } | null,
  ) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    if (!vehicle) return;
    if (prefill) {
      setSearchCriteria((cur) => ({
        ...cur,
        ...(prefill.location ? { location: prefill.location } : {}),
        ...(prefill.pickupDate ? { pickupDate: prefill.pickupDate } : {}),
        ...(prefill.dropoffDate ? { dropoffDate: prefill.dropoffDate } : {}),
      }));
    }
    handleOpenBookingModal(vehicle);
  };

  // Guest booking (no client login — anyone can book): the modal creates the
  // pending booking + draft contract on the server via /api/book-and-sign and
  // redirects to e-signature. Here we only keep the guest-history copy for the
  // profile view and refresh the fleet (server owns availability/status).
  const handleCompleteBooking = async (newBooking: Booking) => {
    saveBookings([newBooking, ...bookings]);
    void refreshVehicles();
  };

  // Local-only guest actions (no auth on the website): cancel / modify only
  // touch the guest history in localStorage.
  const handleCancelBooking = (bookingId: string) => {
    const updated = bookings.map((b) =>
      b.id === bookingId ? { ...b, status: 'cancelled' as const } : b
    );
    saveBookings(updated);
  };

  const handleModifyBookingDates = (bookingId: string, newPickup: string, newDropoff: string) => {
    const updated = bookings.map((b) =>
      b.id === bookingId ? { ...b, pickupDate: newPickup, dropoffDate: newDropoff } : b
    );
    saveBookings(updated);
  };

  const activeBookingsCount = bookings.filter(
    (b) => b.status === 'confirmed' || b.status === 'active'
  ).length;

  // Handed to whichever design renders the home page.
  const homeProps: HomeDesignProps = {
    vehicles,
    favorites,
    storefront: site,
    appearance,
    onToggleFavorite: handleToggleFavorite,
    onBookVehicle: (vehicle) => handleOpenBookingModal(vehicle),
    onBookAny: () => handleOpenBookingModal(),
    onViewAll: () => go('vehicles'),
    onViewDetails: handleViewCarDetailPage,
    onSearch: handleSearchSubmit,
  };

  if (agencyUnavailable) {
    return (
        <div className="theme-surface flex min-h-dvh flex-col items-center justify-center bg-slate-50 px-6 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500" aria-hidden />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Agency unavailable</h1>
          <p className="mt-2 max-w-md text-sm text-slate-600">
            This rental desk is temporarily paused. Please try again later or contact the agency directly.
          </p>
        </div>
    );
  }

  return (
    <PublicBrandProvider brand={effectiveBrand}>
    <CurrencyProvider code={currency} display={displayCurrency} rates={rates}>
    <StorefrontDataProvider value={{
      vehicles,
      vehiclesReady: catalogReady,
      preview: false,
      agency: {
        phone: effectiveBrand.phone,
        email: effectiveBrand.email,
        whatsapp: effectiveBrand.whatsapp ?? effectiveBrand.phone,
        address: effectiveBrand.address,
        name: effectiveBrand.name,
        logoUrl: effectiveBrand.logoUrl,
      },
    }}>
    <div
      className={`theme-surface min-h-dvh flex flex-col font-sans selection:bg-[var(--color-primary)] selection:text-white ${
        hasCustomPage
          ? 'bg-white text-slate-900'
          : activeTheme
            ? toneClass(activeTheme.tokens.tone)
            : design.canvas
      }`}
      style={
        hasCustomPage
          ? undefined
          : ((activeTheme ? themeCssVars(activeTheme.tokens) : appearanceCssVars(appearance, brandColors)) as React.CSSProperties)
      }
    >
      {activeTheme?.customCss && !hasCustomPage ? <style>{activeTheme.customCss}</style> : null}
      {isDraftPreview && (
        <DraftPreviewBanner
          error={previewUnavailable ? published.error : null}
          onExit={exitDraftPreview}
        />
      )}
      <DemoBanner />
      {catalogError && <CatalogError />}
      {(showChrome || usesSharedChrome) && builderHeader && (
        <div className="sticky top-0 z-40">
          <PublishedHome sections={[builderHeader]} designTokens={homeChrome.page?.designTokens} />
        </div>
      )}
      {showChrome && !hasBuilderChrome && (
        <Navbar
          design={design.id}
          currentView={currentView}
          currency={displayCurrency ?? currency}
          onCurrencyChange={handleDisplayCurrency}
          onNavigate={(view) => go(view)}
          onOpenBookingModal={() => handleOpenBookingModal()}
          activeBookingsCount={activeBookingsCount}
          showDeals={sectionEnabled(appearance, 'deals')}
        />
      )}

      {/* 2. Main Page Views */}
      <main className="grow">
        {/* VIEW: a published builder page waits for its own data */}
        {pagePending && !showCustomPage && <PublishedHomeSkeleton />}

        {/* VIEW: a published site-builder page wins over the built-in design */}
        {showCustomPage && published.page && (
          <PublishedHome
            sections={pageBodySections}
            settings={published.page.settings}
            designTokens={published.page.designTokens}
            fill
          />
        )}

        {/* VIEW: a draft preview link that is invalid or expired */}
        {previewUnavailable && (
          <PreviewUnavailable message={published.error} onExit={exitDraftPreview} />
        )}

        {/* VIEW: an unknown single-segment path with no published builder page */}
        {unknownSingleSegment && <StorefrontNotFound />}

        {/* VIEW: HOME — composed by the design the agency chose */}
        {showBuiltIn && currentView === 'home' && activeTheme && (
          <ThemeHome
            theme={activeTheme}
            vehicles={vehicles}
            interactive={isPreviewMode()}
            onSearch={() => go('vehicles')}
            onViewVehicle={handleViewCarDetailPage}
          />
        )}
        {showBuiltIn && currentView === 'home' && !activeTheme && (
          <>
            {design.id === 'editorial' && <EditorialHome {...homeProps} />}
            {design.id === 'roadtrip' && <RoadtripHome {...homeProps} />}
            {design.id === 'classic' && <ClassicHome {...homeProps} />}
          </>
        )}

        {/* VIEW: DEDICATED FULL-PAGE VEHICLE DETAILS */}
        {showBuiltIn && currentView === 'vehicle-detail' && !catalogReady && <VehiclePageStatus kind="loading" />}
        {showBuiltIn && currentView === 'vehicle-detail' && catalogReady && !selectedVehicleForPage && (
          <VehiclePageStatus kind="missing" />
        )}
        {showBuiltIn && currentView === 'vehicle-detail' && selectedVehicleForPage && (
          <VehicleDetailPage
            vehicle={selectedVehicleForPage}
            allVehicles={vehicles}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
            onBackToFleet={() => go('vehicles')}
            onBookNow={(car) => handleOpenBookingModal(car)}
            onSelectSimilarVehicle={(car) => handleViewCarDetailPage(car)}
          />
        )}

        {/* VIEW: VEHICLES CATALOG & CLEAR SEARCH FILTERS */}
        {showBuiltIn && currentView === 'vehicles' && (
          <VehiclesCatalogView
            vehicles={vehicles}
            favorites={favorites}
            onToggleFavorite={handleToggleFavorite}
            onBookVehicle={(car) => handleOpenBookingModal(car)}
            onViewDetails={(car) => handleViewCarDetailPage(car)}
          />
        )}

        {/* VIEW: USER PROFILE DASHBOARD */}
        {showBuiltIn && currentView === 'profile' && (
          <UserProfileView
            bookings={bookings}
            vehicles={vehicles}
            favorites={favorites}
            onCancelBooking={handleCancelBooking}
            onModifyBookingDates={handleModifyBookingDates}
            onBookVehicle={(car) => handleOpenBookingModal(car)}
            onToggleFavorite={handleToggleFavorite}
            onViewVehicleDetails={(car) => handleViewCarDetailPage(car)}
          />
        )}

        {/* VIEW: LOCATIONS DIRECTORY */}
        {showBuiltIn && currentView === 'locations' && (
          <LocationsView
            onSelectLocationForBooking={(locName) => {
              setSearchCriteria({ ...searchCriteria, location: locName });
              handleOpenBookingModal(vehicles[0]);
            }}
          />
        )}

        {/* VIEW: DEALS & PROMOTIONS */}
        {showBuiltIn && currentView === 'deals' && (
          <DealsView
            onApplyDeal={() => {
              handleOpenBookingModal(vehicles[0]);
            }}
          />
        )}

        {/* VIEW: SERVICES */}
        {showBuiltIn && currentView === 'services' && (
          <ServicesView
            onBookService={() => handleOpenBookingModal(vehicles[0])}
          />
        )}

        {/* VIEW: ABOUT US & CONTACT */}
        {showBuiltIn && (currentView === 'about' || currentView === 'contact') && (
          <AboutContactView
            initialTab={currentView === 'contact' ? 'contact' : 'about'}
            faq={site?.faq}
            hours={site?.hours}
            showFaq={sectionEnabled(appearance, 'faq')}
          />
        )}

        {showBuiltIn && currentView === 'faq' && <FaqArticle faq={site?.faq ?? []} />}
        {showBuiltIn && currentView === 'privacy' && <CopyArticle kind="privacy" body={site?.privacy ?? ''} />}
        {showBuiltIn && currentView === 'terms' && <CopyArticle kind="terms" body={site?.terms ?? ''} />}
      </main>

      {(showChrome || usesSharedChrome) && builderFooter && (
        <PublishedHome sections={[builderFooter]} designTokens={homeChrome.page?.designTokens} />
      )}
      {showChrome && !builderFooter && activeTheme && (currentView !== 'home' || unknownSingleSegment) && (
        <ThemeFooter theme={activeTheme} />
      )}
      {showChrome && !builderFooter && !activeTheme && (
        <Footer
          design={design.id}
          social={site?.social}
          onNavigate={(view) => go(view)}
        />
      )}

      {/* Interactive Booking Modal */}
      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        selectedVehicle={selectedVehicleForBooking}
        initialCriteria={searchCriteria}
        onCompleteBooking={handleCompleteBooking}
        onNavigateToProfile={() => go('profile')}
      />

      <AssistantWidget onReserve={handleAssistantReserve} />
    </div>
    </StorefrontDataProvider>
    </CurrencyProvider>
    </PublicBrandProvider>
  );
}

function toneClass(tone: PublicTheme['tokens']['tone']): string {
  if (tone === 'light') return 'bg-[#fff7ed] text-stone-900';
  if (tone === 'slate') return 'bg-slate-100 text-slate-900';
  return 'bg-[#0b0f14] text-white';
}

function agencyFromBrand(brand: PublicBrand): ThemeAgency {
  return {
    name: brand.name,
    city: brand.city,
    address: brand.address,
    phone: brand.phone,
    whatsapp: brand.whatsapp,
    ice: '',
    rc: '',
    patente: '',
    hours: [],
  };
}

function DemoBanner() {
  const { t } = useLocale();
  if (!DEMO_MODE) return null;
  return (
    <p className="bg-amber-100 px-4 py-2 text-center text-xs font-semibold text-amber-950">
      {t('demo.banner')}
    </p>
  );
}

function CatalogError() {
  const { t } = useLocale();
  return (
    <p className="bg-rose-50 px-4 py-2 text-center text-xs font-semibold text-rose-800">
      {t('site.catalogError')}
    </p>
  );
}

function StorefrontNotFound() {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-24 text-center">
      <p className="text-xs font-semibold uppercase tracking-wider text-brand">404</p>
      <h1 className="mt-3 text-3xl font-bold text-slate-900">Page not found</h1>
      <p className="mt-3 text-sm text-slate-600">
        The page you are looking for does not exist or has been moved.
      </p>
      <a
        href="/"
        className="mt-6 rounded-xl bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand/90"
      >
        Back to home
      </a>
    </div>
  );
}

function DraftPreviewBanner({ error, onExit }: { error: string | null; onExit: () => void }) {
  return (
    <div
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-xs font-semibold ${
        error ? 'bg-rose-600 text-white' : 'bg-amber-300 text-amber-950'
      }`}
    >
      {error ? <AlertTriangle className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      <span>{error ?? 'Draft preview — visitors cannot see this version.'}</span>
      <button
        type="button"
        onClick={onExit}
        className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold underline-offset-2 hover:underline ${
          error ? 'bg-white/15 hover:bg-white/25' : 'bg-amber-950/10 hover:bg-amber-950/20'
        }`}
      >
        Exit preview
      </button>
    </div>
  );
}

function PreviewUnavailable({ message, onExit }: { message: string | null; onExit: () => void }) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-24 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
        <AlertTriangle className="h-6 w-6" />
      </span>
      <h1 className="mt-4 text-2xl font-bold text-slate-900">Preview unavailable</h1>
      <p className="mt-2 text-sm text-slate-600">
        {message ?? 'This preview link is invalid or has expired. Ask the agency for a new link.'}
      </p>
      <button
        type="button"
        onClick={onExit}
        className="mt-6 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
      >
        Leave preview
      </button>
    </div>
  );
}
